import { RegionResponse } from '@/modules/api/openapi/models/RegionResponse'
import { MAP_CONSTANTS } from '@/modules/permissions/constants'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'
import { PATHS } from '@/modules/urlRouting/paths'
import { useCurrentPermissions } from '@/modules/user/hooks'

export const useMobilityAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.mobilityManager
}

export const useMobilityActivitySummaryAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  const { data: region } = useCurrentRegion<RegionResponse>()
  return (
    !!permissions?.organizationSettings.mobilityManager &&
    !!permissions?.organizationSettings.home &&
    region?.hasMdsData
  )
}

export const useMobilityDistributionMapAccess = () =>
  useHasMapPermissions(MAP_CONSTANTS.DISTRIBUTION)

export const useMobilityGeographiesMapAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  // Read the map permission before the early returns. Behind them the hook is
  // skipped until permissions load, which changes the hook count of every
  // component that calls this - the nav drawer among them.
  const hasShapesAccess = useHasMapPermissions('shapes')
  if (!permissions) return false
  if (!permissions.organizationSettings.mobilityManager) return false
  return hasShapesAccess
}

export const useMobilityInvoicingAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return (
    !!permissions?.organizationSettings.mobilityManager &&
    !!permissions?.organizationSettings.feeSummary
  )
}

export const useMobilityIssuesMapAccess = () => useHasMapPermissions(MAP_CONSTANTS.ISSUE)

export const useMobilityLiveMapAccess = () => useHasMapPermissions(MAP_CONSTANTS.LIVE)

export const useMobilityLiveMapMobileAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return useMobilityLiveMapAccess() && !!permissions?.organizationSettings.liveMapMobile
}

export const useMobilityMDSHealthAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.mobilityManager
}

export const useMobilityOperatorDropOffsMapAccess = () =>
  useHasMapPermissions(MAP_CONSTANTS.OPERATOR_DROP_OFFS)

export const useMobilityParkingMapAccess = () => useHasMapPermissions(MAP_CONSTANTS.PARKING)

export const useMobilityPoliciesAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  const { data: region } = useCurrentRegion<RegionResponse>()
  return (
    !!permissions?.organizationSettings.mobilityManager &&
    !!permissions?.organizationSettings.policiesLibrary &&
    !!region?.allowedMaps.includes('policy')
  )
}

export const useMobilityPolicyComplianceAccess = () => {
  const { data: permissions } = useCurrentPermissions()

  return (
    !!permissions?.organizationSettings.mobilityManager &&
    !!permissions?.organizationSettings.complianceReporting
  )
}

export const useMobilityPolicyComplianceDetailsAccess = () => {
  const { data: permissions } = useCurrentPermissions()

  return !!permissions?.organizationSettings.complianceDetails
}

export const useMobilityReportingExportBikeshareStationReportAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.bikeshareStationReport
}

export const useMobilityReportingExportDataAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  const { data: region } = useCurrentRegion<RegionResponse>()
  return !!(region?.hasMonthlyZip && permissions?.organizationSettings.operators === null)
}

export const useMobilityReportingExportReportsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.export
}

export const useMobilityReportingMobilityMetricsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  if (!permissions) return false
  if (!permissions.organizationSettings.mobilityManager) return false
  return !!permissions?.organizationSettings.mobilityMetrics
}

export const useMobilityReportingParkingAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  if (!permissions) return false
  if (!permissions.organizationSettings.mobilityManager) return false
  if (!permissions.organizationSettings.mobilityDashboards) return true
  return !!permissions.organizationSettings.mobilityDashboards.includes('micromobility_parking')
}

export const useMobilityReportingTimeDistanceAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  const { data: region } = useCurrentRegion<RegionResponse>()
  if (!permissions || !region?.hasMdsData) return false
  if (!permissions.organizationSettings.mobilityManager) return false
  if (!permissions.organizationSettings.mobilityDashboards) return true
  return !!permissions.organizationSettings.mobilityDashboards.includes('micromobility_use')
}

export const useMobilityReportingTripCountsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  if (!permissions) return false
  return (
    permissions.organizationSettings.mobilityManager &&
    !!permissions.organizationSettings.vehicleAndTripCounts
  )
}

export const useMobilityRoutesMapAccess = () => useHasMapPermissions(MAP_CONSTANTS.ROUTES)

export const useMobilityTripsMapAccess = () => useHasMapPermissions(MAP_CONSTANTS.TRIPS)

export const useCurbParkingDashboardAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbDashboard
}

export const useCurbCitationsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbCitations
}

export const useCurbEnforcementAccess = () => {
  return useCurbCitationsAccess()
}

export const useParkingInsightsProductAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  const hasEnforcementAccess = useCurbEnforcementAccess()
  return (
    !!permissions?.organizationSettings.curbDashboard ||
    !!permissions?.organizationSettings.curbParkingInsights ||
    !!permissions?.organizationSettings.curbTransactionsTable ||
    !!permissions?.organizationSettings.curbOccupancy ||
    !!permissions?.organizationSettings.curbPricingEngine ||
    hasEnforcementAccess
  )
}

export const useCurbParkingInsightsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbParkingInsights
}

export const useCurbParkingTransactionsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbTransactionsTable
}

export const useCurbPricingEngineAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbPricingEngine
}

export const useCurbOccupancyAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbOccupancy
}

export const useRegulationsProductAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return (
    !!permissions?.organizationSettings.curbRegulations ||
    !!permissions?.organizationSettings.curbRegulationsLibrary
  )
}

export const useCurbRegulationsLibraryAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbRegulationsLibrary
}

export const useDigitalSmartZonesProductAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return (
    !!permissions?.organizationSettings.curbMetricsPage ||
    !!permissions?.organizationSettings.curbPerformance ||
    !!permissions?.organizationSettings.curbFeesAndRevenue
  )
}

export const useCurbMetricsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbMetricsPage
}

export const useCurbGeographiesMapAccess = () => {
  const hasParkingInsightsProduct = useParkingInsightsProductAccess()
  const hasDigitalSmartZonesProduct = useDigitalSmartZonesProductAccess()
  const hasRegulationsAccess = useRegulationsProductAccess()

  return hasParkingInsightsProduct || hasDigitalSmartZonesProduct || hasRegulationsAccess
}

export const useCurbFeesAndRevenueAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbFeesAndRevenue
}

export const useCurbActivityAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbPerformance
}

export const useCurbRegulationsAccess = () => {
  const { data: permissions } = useCurrentPermissions()
  return !!permissions?.organizationSettings.curbRegulations
}

export const useGetRegulationsLandingPage = () => {
  const hasRegulationsLibraryAccess = useCurbRegulationsLibraryAccess()
  const hasRegulationsAccess = useCurbRegulationsAccess()

  if (hasRegulationsLibraryAccess) return PATHS.REGULATIONS.LIBRARY
  if (hasRegulationsAccess) return PATHS.REGULATIONS.MAP
}

export const useGetDigitalSmartZonesLandingPage = () => {
  const hasMetricsAccess = useCurbMetricsAccess()
  const hasActivityAccess = useCurbActivityAccess()
  const hasFeesAndRevenuePageAccess = useCurbFeesAndRevenueAccess()

  if (hasMetricsAccess) return PATHS.MANAGEMENT.METRICS
  if (hasActivityAccess) return PATHS.MANAGEMENT.ACTIVITY
  if (hasFeesAndRevenuePageAccess) return PATHS.MANAGEMENT.FEES_AND_REVENUE
}

export const useGetParkingInsightsLandingPage = () => {
  const hasDashboardAccess = useCurbParkingDashboardAccess()
  const hasParkingInsightsAccess = useCurbParkingInsightsAccess()
  const hasTransactionsTableAccess = useCurbParkingTransactionsAccess()
  const hasOccupancyAccess = useCurbOccupancyAccess()
  const hasPricingEngineAccess = useCurbPricingEngineAccess()
  const hasEnforcementAccess = useCurbEnforcementAccess()

  if (hasDashboardAccess) return PATHS.ELEVATE.DASHBOARD
  if (hasParkingInsightsAccess) return PATHS.ELEVATE.REVENUE
  if (hasTransactionsTableAccess) return PATHS.ELEVATE.SESSIONS
  if (hasOccupancyAccess) return PATHS.ELEVATE.OCCUPANCY
  if (hasPricingEngineAccess) return PATHS.ELEVATE.PRICING_ENGINE
  if (hasEnforcementAccess) return PATHS.ELEVATE.ENFORCEMENT
}

export const useGetMobilityLandingPage = () => PATHS.MOBILITY.MAPS.LIVE

/**
 * Priority order:
 * Mobility Manager > Parking Insights > Regulations > Digital Smart Zones
 */
export const useGetRegionLandingPage = () => {
  const mobilityLandingPage = useGetMobilityLandingPage()
  const parkingInsightsLandingPage = useGetParkingInsightsLandingPage()
  const curbRegulationsLandingPage = useGetRegulationsLandingPage()
  const digitalSmartZonesLandingPage = useGetDigitalSmartZonesLandingPage()

  const hasMobility = useMobilityAccess()
  const hasParkingInsights = useParkingInsightsProductAccess()
  const hasRegulations = useRegulationsProductAccess()
  const hasDigitalSmartZones = useDigitalSmartZonesProductAccess()

  return (
    (hasMobility && mobilityLandingPage) ||
    (hasParkingInsights && parkingInsightsLandingPage) ||
    (hasRegulations && curbRegulationsLandingPage) ||
    (hasDigitalSmartZones && digitalSmartZonesLandingPage) ||
    undefined
  )
}

export const useHasMapPermissions = (mapName: string): boolean => {
  const { data: permissions } = useCurrentPermissions()
  const { data: region } = useCurrentRegion<RegionResponse>()

  if (mapName === MAP_CONSTANTS.ROUTES && !region?.hasMdsData) return false

  const orgHasAllMaps = !!permissions && !permissions.organizationSettings.maps
  const orgHasPermissions =
    !!permissions?.organizationSettings.maps?.includes(mapName) || orgHasAllMaps
  const regionHasPermissions = !!region?.allowedMaps?.includes(mapName)

  return orgHasPermissions && regionHasPermissions
}
